import { ReactNode } from "react";
import { PieChart, Pie, Cell, Label } from "recharts";
import { Activity, AppWindow, Globe, MonitorSmartphone, Tag } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { CountryFlag } from "@/components/CountryFlag";
import { BrowserIcon } from "@/components/icons/BrowserIcon";
import { OsIcon } from "@/components/icons/OsIcon";
import { PluginAggregates, PluginStateBucket } from "@/services/pluginStatusApi";
import { HEALTH_META, HEALTH_ORDER, STATE_META, healthTotals } from "../workerState";

// Répartition de la flotte de plugins CONNECTÉS selon 4 axes (pays, navigateur,
// OS, version plugin). Les COMPTES viennent du backoffice (calculés sur toute
// la flotte, cf. GET /plugin-status/connected → aggregates) ; ce composant ne
// fait que la PRÉSENTATION : repli "top-7 + Autres", couleurs, drapeaux/icônes.

// Palette : variables shadcn (--chart-1..5) + 2 teintes pour les axes à forte
// cardinalité (pays, versions pendant un rollout Web Store).
const SLICE_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(217 91% 60%)",
  "hsl(262 83% 65%)",
];
const OTHER_COLOR = "hsl(215 16% 65%)"; // gris neutre — bucket "Autres"
const MAX_SLICES = 7; // top-7 + "Autres" : garde les donuts lisibles

// « Par état » chart: labels, colours and health groups live in
// ../workerState.ts, shared with the table rows. The display order stays the
// back's sort by count.

// Health summary strip above the donuts: 4 figures (count · %) that answer at a
// glance « how many plugins are REALLY broken? » — inactivity is no longer
// mixed in (grey « Inactif »).
function FleetHealthSummary({ buckets }: { buckets: PluginStateBucket[] }) {
  const totals = healthTotals(buckets);
  const grand = HEALTH_ORDER.reduce((s, g) => s + totals[g], 0);
  if (grand === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {HEALTH_ORDER.map((g) => {
        const meta = HEALTH_META[g];
        return (
          <div key={g} className="flex items-center gap-2 rounded-md border p-2.5">
            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${meta.dot}`} />
            <div className="min-w-0">
              <div className="truncate text-xs text-muted-foreground">{meta.label}</div>
              <div className={`font-mono text-sm font-semibold tabular-nums ${meta.color}`}>
                {totals[g]} · {pct(totals[g], grand)}%
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// by_state -> slices. Bounded cardinality (14 states): no "Autres" folding,
// each state keeps ITS colour (a top-7 fold would break the semantics).
function stateSlices(buckets: PluginStateBucket[]): Slice[] {
  return [...buckets]
    .sort((a, b) => b.count - a.count)
    .map((b) => {
      const meta = STATE_META[b.key];
      return {
        name: meta?.label ?? b.key,
        value: b.count,
        fill: meta?.color ?? OTHER_COLOR,
      };
    });
}

interface Item {
  label: string;
  count: number;
  leading?: ReactNode;
}

interface Slice {
  name: string; // clé unique = libellé (distinct par groupe)
  value: number;
  fill: string;
  leading?: ReactNode;
}

// Items (déjà comptés, triés desc par le back) -> slices top-7 + "Autres".
function toSlices(items: Item[]): Slice[] {
  const sorted = [...items].sort((a, b) => b.count - a.count);
  const top = sorted.slice(0, MAX_SLICES);
  const rest = sorted.slice(MAX_SLICES);
  const slices: Slice[] = top.map((it, i) => ({
    name: it.label,
    value: it.count,
    fill: SLICE_COLORS[i % SLICE_COLORS.length],
    leading: it.leading,
  }));
  if (rest.length > 0) {
    slices.push({
      name: `Autres (${rest.length})`,
      value: rest.reduce((s, it) => s + it.count, 0),
      fill: OTHER_COLOR,
    });
  }
  return slices;
}

const pct = (value: number, total: number) =>
  total > 0 ? Math.round((value / total) * 100) : 0;

function DistributionDonut({
  title,
  icon,
  slices,
  distinctCount,
  unit,
  total,
}: {
  title: string;
  icon: ReactNode;
  slices: Slice[];
  distinctCount: number;
  unit: string;
  total: number;
}) {
  // Config recharts : 1 entrée par slice (clé = libellé), pour que le tooltip
  // résolve le bon label/couleur via nameKey="name".
  const chartConfig: ChartConfig = {};
  for (const s of slices) {
    chartConfig[s.name] = { label: s.name, color: s.fill };
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <span className="text-muted-foreground">{icon}</span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {slices.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            Aucune donnée
          </div>
        ) : (
          <>
            <ChartContainer
              config={chartConfig}
              className="mx-auto aspect-square max-h-[150px]"
            >
              <PieChart>
                <ChartTooltip content={<ChartTooltipContent nameKey="name" />} />
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={42}
                  outerRadius={64}
                  strokeWidth={2}
                >
                  {slices.map((s) => (
                    <Cell key={s.name} fill={s.fill} />
                  ))}
                  <Label
                    content={({ viewBox }) => {
                      if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                        return (
                          <text
                            x={viewBox.cx}
                            y={viewBox.cy}
                            textAnchor="middle"
                            dominantBaseline="middle"
                          >
                            <tspan
                              x={viewBox.cx}
                              y={viewBox.cy}
                              className="fill-foreground text-2xl font-bold"
                            >
                              {distinctCount}
                            </tspan>
                            <tspan
                              x={viewBox.cx}
                              y={(viewBox.cy || 0) + 18}
                              className="fill-muted-foreground text-xs"
                            >
                              {unit}
                            </tspan>
                          </text>
                        );
                      }
                      return null;
                    }}
                  />
                </Pie>
              </PieChart>
            </ChartContainer>
            <ul className="mt-3 space-y-1">
              {slices.map((s) => (
                <li key={s.name} className="flex items-center gap-1.5 text-xs">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                    style={{ backgroundColor: s.fill }}
                  />
                  {s.leading}
                  <span className="truncate" title={s.name}>
                    {s.name}
                  </span>
                  <span className="ml-auto shrink-0 font-mono tabular-nums text-muted-foreground">
                    {s.value} · {pct(s.value, total)}%
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function PluginDistributionCharts({
  aggregates,
  total,
}: {
  aggregates: PluginAggregates;
  total: number;
}) {
  const countryItems: Item[] = aggregates.by_country.map((b) => ({
    label: b.label,
    count: b.count,
    // Drapeau best-effort : CountryFlag mappe aussi le NOM -> code ISO (entrées
    // de cache sans country_code). Pas de drapeau pour "Inconnu"/"Réseau privé".
    leading:
      b.country_code || b.country ? (
        <CountryFlag
          location={{ country_code: b.country_code, country: b.country }}
          className="!w-4"
        />
      ) : undefined,
  }));

  const browserItems: Item[] = aggregates.by_browser.map((b) => ({
    label: b.label,
    count: b.count,
    leading: (
      <BrowserIcon
        name={b.label}
        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
      />
    ),
  }));

  const osItems: Item[] = aggregates.by_os.map((b) => ({
    label: b.label,
    count: b.count,
    leading: (
      <OsIcon
        name={b.label}
        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
      />
    ),
  }));

  const versionItems: Item[] = aggregates.by_version.map((b) => ({
    label: b.label,
    count: b.count,
  }));

  // Fleet states (inactive / active / quota…). May cover MORE workers than
  // `total` (ghost / offline: an activity trace without heartbeat, hence not in
  // the table): percentages are over the counted states, not over `total`.
  const stateBuckets = aggregates.by_state ?? [];
  const stateTotal = stateBuckets.reduce((s, b) => s + b.count, 0);
  const hasStates = stateBuckets.length > 0;

  return (
    <div className="space-y-4">
      {hasStates && <FleetHealthSummary buckets={stateBuckets} />}
      <div
        className={`grid gap-4 md:grid-cols-2 ${
          hasStates ? "xl:grid-cols-5" : "xl:grid-cols-4"
        }`}
      >
      {hasStates && (
        <DistributionDonut
          title="Par état"
          icon={<Activity className="h-4 w-4" />}
          slices={stateSlices(stateBuckets)}
          distinctCount={stateTotal}
          unit="workers"
          total={stateTotal}
        />
      )}
      <DistributionDonut
        title="Par localisation"
        icon={<Globe className="h-4 w-4" />}
        slices={toSlices(countryItems)}
        distinctCount={aggregates.by_country.length}
        unit="pays"
        total={total}
      />
      <DistributionDonut
        title="Par navigateur"
        icon={<AppWindow className="h-4 w-4" />}
        slices={toSlices(browserItems)}
        distinctCount={aggregates.by_browser.length}
        unit="navigateurs"
        total={total}
      />
      <DistributionDonut
        title="Par OS"
        icon={<MonitorSmartphone className="h-4 w-4" />}
        slices={toSlices(osItems)}
        distinctCount={aggregates.by_os.length}
        unit="OS"
        total={total}
      />
        <DistributionDonut
          title="Par version"
          icon={<Tag className="h-4 w-4" />}
          slices={toSlices(versionItems)}
          distinctCount={aggregates.by_version.length}
          unit="versions"
          total={total}
        />
      </div>
    </div>
  );
}
